import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useContext,
} from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  CreditCard,
  ShoppingCart,
  Check,
  Lock,
  ArrowBack,
  LocationOn,
} from '@mui/icons-material'
import {
  UNSAFE_NavigationContext as NavigationContext,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import apiService from '../services/ApiService'
import {
  COUNTRY_OPTIONS,
  PHONE_CODE_OPTIONS,
  MOBILE_NUMBER_LOCAL_MAX_DIGITS,
  getCountryPhoneCode,
  fetchIpLocationHints,
  getStateOptions,
  normalizeLocalMobileNumber,
  resolveCountryCode,
  splitStoredMobileNumber,
  validateAndFormatMobileNumber,
} from '../utils/locationData'
import { Helmet } from 'react-helmet-async'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'

// Initialize Stripe (replace with your publishable key)
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here',
)

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const PAYMENT_IN_PROGRESS_WARNING_MESSAGE =
  'Payment is in progress. Please do not press Back or refresh this page until it completes.'
const PAYMENT_NAVIGATION_BLOCKED_MESSAGE =
  'Navigation blocked while payment is in progress. Going back now can interrupt your payment.'

const CHECKOUT_ADDRESS_FIELDS = [
  'street',
  'city',
  'state',
  'postalCode',
  'country',
]
const CHECKOUT_PROFILE_FIELDS = [...CHECKOUT_ADDRESS_FIELDS, 'mobileNumber']
const CHECKOUT_ERROR_METADATA_FIELDS = new Set([
  'message',
  'error',
  'timestamp',
  'status',
  'path',
  'errorCode',
])

const resolveCheckoutFieldName = (rawKey) => {
  if (!rawKey) {
    return null
  }

  const normalizedKey = String(rawKey).trim()
  const directMatch = CHECKOUT_PROFILE_FIELDS.find(
    (field) => field === normalizedKey,
  )

  if (directMatch) {
    return directMatch
  }

  return (
    CHECKOUT_PROFILE_FIELDS.find(
      (field) =>
        normalizedKey.endsWith(`.${field}`) ||
        normalizedKey.endsWith(`[${field}]`) ||
        normalizedKey.endsWith(`:${field}`),
    ) || null
  )
}

const normalizeGeoText = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')

const findMatchingStateFromGeo = (geoRegion, stateOptions) => {
  const normalizedRegion = normalizeGeoText(geoRegion)
  if (!normalizedRegion) {
    return ''
  }

  const realStates = (stateOptions || []).filter(
    (state) =>
      state?.value &&
      state.value !== 'NOT_APPLICABLE' &&
      state.value !== 'OTHER',
  )

  const exactMatch = realStates.find(
    (state) => normalizeGeoText(state.value) === normalizedRegion,
  )
  if (exactMatch) {
    return exactMatch.value
  }

  const containsMatch = realStates.find((state) => {
    const normalizedState = normalizeGeoText(state.value)
    return (
      normalizedState.includes(normalizedRegion) ||
      normalizedRegion.includes(normalizedState)
    )
  })

  return containsMatch?.value || ''
}

const profileFetchOptions = {
  bypassCache: true,
  skipCache: true,
}

const formFocusStyles = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: themeColors.primary,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: themeColors.primary,
  },
}

const useNavigationBlocker = (when, onBlock) => {
  const { navigator } = useContext(NavigationContext)

  useEffect(() => {
    if (!when || !navigator?.block) {
      return undefined
    }

    const unblock = navigator.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock()
          tx.retry()
        },
      }
      onBlock(autoUnblockingTx)
    })

    return unblock
  }, [navigator, onBlock, when])
}

const convertArrayToDate = (dateInput) => {
  if (!dateInput) return null

  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput
  }

  if (typeof dateInput === 'string') {
    const date = new Date(dateInput)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (!Array.isArray(dateInput)) {
    return null
  }

  const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] =
    dateInput

  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day)
  ) {
    return null
  }

  const date = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      Math.floor(nanosecond / 1000000),
    ),
  )

  return Number.isNaN(date.getTime()) ? null : date
}

const Checkout = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated, user, refreshQuota } = useAuth()

  const [loading, setLoading] = useState(true)
  const [item, setItem] = useState(null) // Plan or package
  const [itemType, setItemType] = useState(null) // 'plan' or 'package'
  const [error, setError] = useState(null)
  const [loadErrorSnackbarOpen, setLoadErrorSnackbarOpen] = useState(false)

  // Get plan code (preferred) or legacy plan ID from URL
  const planReference = searchParams.get('plan')
  const packageId = searchParams.get('package')

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout' } })
      return
    }

    const fetchItem = async () => {
      try {
        setLoading(true)

        if (planReference) {
          let plan = null
          try {
            plan = await apiService.getSubscriptionPlanByCode(planReference)
          } catch (planCodeError) {
            // Backward compatibility for older links that used Mongo plan IDs.
            plan = await apiService.getSubscriptionPlanById(planReference)
          }
          // console.log('Fetched plan:', plan)
          if (!plan || !plan.id) {
            throw new Error('Plan not found or invalid plan data')
          }
          setItem(plan)
          setItemType('plan')
        } else if (packageId) {
          // console.log('Fetching package with ID:', packageId)
          const pkg = await apiService.get(
            `/api/v1/credits/packages/${packageId}`,
          )
          // console.log('Fetched package:', pkg)
          if (!pkg || !pkg.id) {
            throw new Error('Package not found or invalid package data')
          }
          setItem(pkg)
          setItemType('package')
        } else {
          setError('No item selected. Please go back to pricing.')
        }

        setError(null)
      } catch (err) {
        console.error('Error fetching checkout item:', err)
        setError('Failed to load checkout information.')
        setError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load checkout information.',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchItem()
  }, [planReference, packageId, isAuthenticated, navigate])

  useEffect(() => {
    if (!loading && (error || !item)) {
      setLoadErrorSnackbarOpen(true)
    }
  }, [error, item, loading])

  const handleLoadErrorSnackbarClose = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setLoadErrorSnackbarOpen(false)
  }, [])

  if (loading) {
    return (
      <Container maxWidth='sm' sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant='h6' sx={{ mt: 3 }}>
          Loading checkout...
        </Typography>
      </Container>
    )
  }

  if (error || !item) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        {/* <Alert severity='error'>{error || 'Item not found'}</Alert> */}
        <Snackbar
          open={loadErrorSnackbarOpen}
          onClose={handleLoadErrorSnackbarClose}
          autoHideDuration={7000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity='error'
            onClose={handleLoadErrorSnackbarClose}
            sx={{ width: '100%' }}
          >
            {error || 'Item not found'}
          </Alert>
        </Snackbar>
        <Button
          variant='contained'
          onClick={() => navigate('/pricing')}
          sx={{
            mt: 3,
            backgroundColor: themeColors.primary,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: themeColors.hover,
            },
          }}
        >
          Back to Pricing
        </Button>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>Checkout | GCC Tax Laws</title>
        <meta name='robots' content='noindex, nofollow' />
      </Helmet>

      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: { xs: 3, md: 6 } }}>
        <Container maxWidth='lg' sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Button
            // variant='contained' // Changed to contained to show the background color
            startIcon={<ArrowBack />}
            onClick={() => navigate('/pricing')}
            sx={{
              mb: { xs: 2, md: 3 },
              px: 0.5,
              // backgroundColor: '#232536',
              fontWeight: 500,
              color: themeColors.primary, // #ffffff
              textTransform: 'none', // Optional: keeps text from being all-caps
              '&:hover': {
                // backgroundColor: '#3a3e5a',
              },
            }}
          >
            Back to Pricing
          </Button>

          <Grid container spacing={{ xs: 2.5, md: 4 }}>
            {/* Order Summary */}
            <Grid item xs={12} md={5} sx={{ alignSelf: { md: 'stretch' } }}>
              <OrderSummary item={item} itemType={itemType} />
            </Grid>

            {/* Payment Form */}
            <Grid item xs={12} md={7}>
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  item={item}
                  itemType={itemType}
                  user={user}
                  refreshQuota={refreshQuota}
                />
              </Elements>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  )
}

// Order Summary Component
const OrderSummary = ({ item, itemType }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(price || 0)
  }

  // Get item name - supports both MongoDB @Field and DTO names
  const getItemName = () => {
    if (itemType === 'plan') {
      return item.planName || 'Subscription Plan'
    }
    return item.packageName || 'Credit Package'
  }

  // Get price - supports both MongoDB @Field and DTO names
  const getPrice = () => {
    return item.priceAed || item.price || 0
  }

  // Get billing text
  const getBillingText = () => {
    if (itemType === 'package') return 'One-time payment'
    switch (item.durationType) {
      case 'DAILY':
        return 'Billed once'
      case 'WEEKLY':
        return 'Billed weekly'
      case 'MONTHLY':
        return 'Billed monthly'
      case 'QUARTERLY':
        return 'Billed quarterly'
      case 'HALF_YEARLY':
        return 'Billed every 6 months'
      case 'ANNUAL':
        return 'Billed annually'
      default:
        return `Valid for ${item.durationDays || 0} day(s)`
    }
  }

  const getFeatures = () => {
    if (itemType === 'package') {
      const creditsAmount = item.creditsAmount || item.credits || 0
      const bonusCredits = item.bonusCredits || 0
      const totalCredits = creditsAmount + bonusCredits
      return [
        `${totalCredits} credits total`,
        bonusCredits > 0 ? `Includes ${bonusCredits} bonus credits` : null,
        'Never expires',
        'Use anytime',
        'Works across all documents',
      ].filter(Boolean)
    }

    // Return features from item or default features
    return (
      item.features || [
        'Unlimited document access',
        'Advanced search & filters',
        'Download & print',
        'Priority support',
        'Ad-free experience',
      ]
    )
  }

  const price = getPrice() // Set to true when revenue exceeds 375K AED, we can only change VAT if our gross revenue exceeds 375K AED
  const shouldChargeTax = process.env.REACT_APP_CHARGE_VAT === 'true' || false
  const taxRate = shouldChargeTax ? 0.05 : 0 // fetch value from systemConfiguration TAX_RATE
  const tax = price * taxRate
  const total = price + tax

  return (
    <Paper
      sx={{
        p: { xs: 2.25, sm: 3 },
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
        position: { xs: 'static', md: 'sticky' },
        top: { md: 92 },
        maxHeight: { md: 'calc(100vh - 108px)' },
        overflowY: { md: 'auto' },
      }}
    >
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 3 }}>
        Order Summary
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
          {getItemName()}
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 2, lineHeight: 1.55 }}
        >
          {item.description || 'No description available'}
        </Typography>
        <Chip
          label={getBillingText()}
          size='small'
          sx={{ bgcolor: '#f5f5f5' }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
          What's included:
        </Typography>
        <List dense>
          {getFeatures().map((feature, index) => (
            <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
              <Check sx={{ fontSize: 18, color: themeColors.primary, mr: 1 }} />
              <ListItemText
                primary={feature}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant='body1'>Subtotal:</Typography>
        <Typography variant='body1'>{formatPrice(price)}</Typography>
      </Box>

      {shouldChargeTax && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant='body1'>Tax (5% VAT):</Typography>
          <Typography variant='body1'>{formatPrice(tax)}</Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          Total:
        </Typography>
        <Typography
          variant='h5'
          sx={{ fontWeight: 700, color: themeColors.primary }}
        >
          {formatPrice(total)}
        </Typography>
      </Box>
    </Paper>
  )
}

// Checkout Form Component
const CheckoutForm = ({ item, itemType, user, refreshQuota }) => {
  const { isAuthenticated } = useAuth()
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  const location = useLocation()
  const submitInFlightRef = useRef(false)
  const geoPrefillAttemptedRef = useRef(false)
  const geoEditableTouchedRef = useRef({
    country: false,
    state: false,
    city: false,
    phoneCountryCode: false,
  })

  const [processing, setProcessing] = useState(false)
  const [navigationAttemptBlocked, setNavigationAttemptBlocked] = useState(false)
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [blockedNavigationTx, setBlockedNavigationTx] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const [couponError, setCouponError] = useState(null)
  const [couponErrorSnackbarOpen, setCouponErrorSnackbarOpen] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('stripe')
  const [autoPayOptIn, setAutoPayOptIn] = useState(true)
  const [agreeTerms, setAgreeTerms] = useState(false)

  const [needsAddress, setNeedsAddress] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'IN',
  })
  const [mobileNumber, setMobileNumber] = useState('')
  const [phoneCountryCode, setPhoneCountryCode] = useState('91')
  const [needsMobileNumber, setNeedsMobileNumber] = useState(false)
  const [addressErrors, setAddressErrors] = useState({})
  const [activeSubscriptions, setActiveSubscriptions] = useState([])
  const stateOptions = useMemo(
    () => getStateOptions(addressData.country, addressData.state),
    [addressData.country, addressData.state],
  )
  const isMobileNumberValidForSubmit = useMemo(
    () =>
      !needsMobileNumber ||
      validateAndFormatMobileNumber({
        phoneCode: phoneCountryCode,
        localNumber: mobileNumber,
        fallbackCountryCode: addressData.country,
      }).isValid,
    [needsMobileNumber, phoneCountryCode, mobileNumber, addressData.country],
  )
  const fetchFreshProfile = useCallback(
    () => apiService.get('/api/v1/profile', {}, profileFetchOptions),
    [],
  )
  const handlePaymentErrorSnackbarClose = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setError(null)
  }, [])

  const handlePaymentStatusSnackbarClose = useCallback(
    (_, reason) => {
      if (reason === 'clickaway') {
        return
      }

      if (success) {
        setSuccess(false)
      }
    },
    [success],
  )

  const handleCouponErrorSnackbarClose = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setCouponErrorSnackbarOpen(false)
  }, [])

  useEffect(() => {
    if (couponError) {
      setCouponErrorSnackbarOpen(true)
    } else {
      setCouponErrorSnackbarOpen(false)
    }
  }, [couponError])

  const paymentStatusSnackbar = useMemo(() => {
    if (success) {
      return {
        severity: 'success',
        message: 'Payment successful! Redirecting...',
        showSpinner: true,
        autoHideDuration: 3000,
      }
    }

    if (processing) {
      return {
        severity: 'warning',
        message: navigationAttemptBlocked
          ? PAYMENT_NAVIGATION_BLOCKED_MESSAGE
          : PAYMENT_IN_PROGRESS_WARNING_MESSAGE,
        showSpinner: false,
        autoHideDuration: null,
      }
    }

    return null
  }, [navigationAttemptBlocked, processing, success])

  const shouldBlockCheckoutNavigation = processing && !success

  useNavigationBlocker(
    shouldBlockCheckoutNavigation,
    useCallback((tx) => {
      setNavigationAttemptBlocked(true)
      setBlockedNavigationTx(tx)
      setLeaveDialogOpen(true)
    }, []),
  )

  const handleStayOnCheckout = useCallback(() => {
    setLeaveDialogOpen(false)
    setBlockedNavigationTx(null)
  }, [])

  const handleLeaveCheckout = useCallback(() => {
    if (!blockedNavigationTx) {
      return
    }

    const pendingTx = blockedNavigationTx
    setLeaveDialogOpen(false)
    setBlockedNavigationTx(null)
    pendingTx.retry()
  }, [blockedNavigationTx])

  const focusCheckoutField = useCallback((fieldName) => {
    if (!fieldName) {
      return
    }

    const fieldElement = document.querySelector(`[name="${fieldName}"]`)
    if (fieldElement) {
      fieldElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })

      if (typeof fieldElement.focus === 'function') {
        fieldElement.focus()
      }
      return
    }

    document.getElementById('address-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [])

  const focusFirstCheckoutFieldError = useCallback(
    (fieldErrors) => {
      const firstField = Object.keys(fieldErrors || {})[0]
      if (!firstField) {
        return
      }

      setTimeout(() => {
        focusCheckoutField(firstField)
      }, 100)
    },
    [focusCheckoutField],
  )

  const extractCheckoutFieldErrors = useCallback((errorData) => {
    if (!errorData || typeof errorData !== 'object' || Array.isArray(errorData)) {
      return {}
    }

    const fieldErrors = {}
    Object.entries(errorData).forEach(([rawKey, value]) => {
      if (typeof value !== 'string') {
        return
      }

      const matchedField = resolveCheckoutFieldName(rawKey)
      if (matchedField) {
        fieldErrors[matchedField] = value
      }
    })

    if (Object.keys(fieldErrors).length > 0) {
      return fieldErrors
    }

    Object.entries(errorData).forEach(([rawKey, value]) => {
      if (
        typeof value !== 'string' ||
        CHECKOUT_ERROR_METADATA_FIELDS.has(rawKey)
      ) {
        return
      }

      const matchedField = resolveCheckoutFieldName(rawKey)
      if (matchedField) {
        fieldErrors[matchedField] = value
      }
    })

    return fieldErrors
  }, [])

  const applyCheckoutFieldErrors = useCallback(
    (fieldErrors) => {
      if (!fieldErrors || Object.keys(fieldErrors).length === 0) {
        return false
      }

      setAddressErrors(fieldErrors)
      setError('Please correct the highlighted fields and try again.')

      if (fieldErrors.mobileNumber) {
        setNeedsMobileNumber(true)
      }

      if (
        CHECKOUT_ADDRESS_FIELDS.some((fieldName) =>
          Boolean(fieldErrors[fieldName]),
        )
      ) {
        setNeedsAddress(true)
      }

      focusFirstCheckoutFieldError(fieldErrors)
      return true
    },
    [focusFirstCheckoutFieldError],
  )

  const buildCheckoutContextUrl = useCallback(
    (path, includeSelectionContext = false) => {
      try {
        const url = new URL(path, window.location.origin)
        const currentQuery = new URLSearchParams(location.search || '')

        if (includeSelectionContext) {
          if (itemType === 'plan') {
            const planRef =
              item?.planCode || item?.id || currentQuery.get('plan') || ''
            if (planRef) {
              url.searchParams.set('plan', planRef)
            }
          } else if (itemType === 'package') {
            const packageRef = item?.id || currentQuery.get('package') || ''
            if (packageRef) {
              url.searchParams.set('package', packageRef)
            }
          }
        }

        const hasExpectedSelection =
          url.searchParams.has('plan') || url.searchParams.has('package')

        if (includeSelectionContext && !hasExpectedSelection) {
          const fallbackCurrentUrl = new URL(window.location.href)
          if (
            fallbackCurrentUrl.pathname.includes('/checkout') &&
            fallbackCurrentUrl.search
          ) {
            return fallbackCurrentUrl.toString()
          }
        }

        return url.toString()
      } catch (urlError) {
        return window.location.href
      }
    },
    [itemType, item?.id, item?.planCode, location.search],
  )

  const mapStripeFailureMessage = useCallback((err) => {
    const code =
      err?.code || err?.decline_code || err?.raw?.code || err?.raw?.decline_code

    switch (code) {
      case 'expired_card':
        return 'Your card has expired. Please use a different card.'
      case 'incorrect_cvc':
        return 'Your card CVC is incorrect. Please check and try again.'
      case 'insufficient_funds':
        return 'Your card has insufficient funds. Please use another card.'
      case 'card_declined':
        return 'Your card was declined. Please try another card.'
      default:
        return null
    }
  }, [])

  useEffect(() => {
    if (!shouldBlockCheckoutNavigation) {
      setNavigationAttemptBlocked(false)
      setLeaveDialogOpen(false)
      setBlockedNavigationTx(null)
      return undefined
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [shouldBlockCheckoutNavigation])

  const tryIpBasedPrefill = useCallback(
    async ({
      profile,
      profileNeedsAddress,
      profileNeedsMobile,
      resolvedCountryCode,
      defaultPhoneCode,
    }) => {
      if (geoPrefillAttemptedRef.current) {
        return
      }

      const hasProfileCountry = Boolean((profile?.country || '').trim())
      const hasProfileCity = Boolean((profile?.city || '').trim())
      const hasProfileState = Boolean((profile?.state || '').trim())

      const shouldTry =
        (profileNeedsAddress &&
          (!hasProfileCountry || !hasProfileCity || !hasProfileState)) ||
        (profileNeedsMobile && !hasProfileCountry)

      if (!shouldTry) {
        return
      }

      geoPrefillAttemptedRef.current = true

      try {
        const geoHint = await fetchIpLocationHints()
        if (!geoHint) {
          return
        }

        const hintedCountryCode = resolveCountryCode(
          geoHint.countryCode || resolvedCountryCode,
        )

        setAddressData((prev) => {
          const next = { ...prev }
          const shouldSetCountry =
            !hasProfileCountry &&
            !geoEditableTouchedRef.current.country &&
            hintedCountryCode

          if (shouldSetCountry) {
            next.country = hintedCountryCode
          }

          if (
            !hasProfileCity &&
            !geoEditableTouchedRef.current.city &&
            !String(prev.city || '').trim() &&
            geoHint.city
          ) {
            next.city = geoHint.city
          }

          const shouldSetState =
            !hasProfileState &&
            !geoEditableTouchedRef.current.state &&
            geoHint.region
          if (shouldSetState) {
            const stateOptionsForCountry = getStateOptions(
              next.country || hintedCountryCode,
            )
            const matchedState = findMatchingStateFromGeo(
              geoHint.region,
              stateOptionsForCountry,
            )
            if (matchedState) {
              next.state = matchedState
            }
          }

          return next
        })

        if (
          !profileNeedsMobile ||
          geoEditableTouchedRef.current.phoneCountryCode
        ) {
          return
        }

        const hintedPhoneCode = getCountryPhoneCode(hintedCountryCode)
        setPhoneCountryCode((prev) => {
          const normalizedPrev = normalizeLocalMobileNumber(prev)
          const normalizedDefault = normalizeLocalMobileNumber(defaultPhoneCode)
          const isStillDefault =
            !normalizedPrev || normalizedPrev === normalizedDefault
          return isStillDefault ? hintedPhoneCode : prev
        })
      } catch (error) {
        // Best-effort prefill only; ignore lookup failures.
      }
    },
    [],
  )

  useEffect(() => {
    if (itemType !== 'plan' || !item?.id) {
      setAutoPayOptIn(false)
      return
    }

    // Default to enabled for recurring plans, hidden/disabled for one-time plans.
    setAutoPayOptIn(Boolean(item?.isRecurring))
  }, [itemType, item?.id, item?.isRecurring])

  useEffect(() => {
    if (!needsAddress || stateOptions.length === 0) {
      return
    }

    const hasCurrentState = stateOptions.some(
      (state) => state.value === addressData.state,
    )

    if (!hasCurrentState) {
      setAddressData((prev) => ({
        ...prev,
        state: stateOptions[0].value,
      }))
    }
  }, [needsAddress, stateOptions, addressData.state])

  // Helper function to check if address is complete
  const isAddressComplete = (address) => {
    return (
      address &&
      address.street &&
      address.street.trim() !== '' &&
      address.city &&
      address.city.trim() !== '' &&
      address.state &&
      address.state.trim() !== '' &&
      address.postalCode &&
      address.postalCode.trim() !== '' &&
      address.country &&
      address.country.trim() !== ''
    )
  }

  useEffect(() => {
    const checkUserAddress = async () => {
      try {
        const profile = await fetchFreshProfile()

        const resolvedCountryCode = resolveCountryCode(profile.country)
        const defaultPhoneCode = getCountryPhoneCode(resolvedCountryCode)

        // Pre-populate address fields if available
        if (
          profile.street ||
          profile.city ||
          profile.state ||
          profile.postalCode ||
          profile.country
        ) {
          setAddressData({
            street: profile.street || '',
            city: profile.city || '',
            state: profile.state || '',
            postalCode: profile.postalCode || '',
            country: resolvedCountryCode,
          })
        } else {
          setAddressData((prev) => ({
            ...prev,
            country: resolvedCountryCode,
          }))
        }

        // Pre-populate mobile number if available
        const profileMobileNumber = profile.mobileNumber
        const hasProfileMobile =
          profileMobileNumber && profileMobileNumber.trim() !== ''

        if (hasProfileMobile) {
          const normalizedStored = splitStoredMobileNumber(
            profileMobileNumber,
            defaultPhoneCode,
          )
          setPhoneCountryCode(normalizedStored.phoneCode || defaultPhoneCode)
          setMobileNumber(normalizedStored.localNumber)
          setNeedsMobileNumber(false)
          // console.log('Mobile number found in profile:', profileMobileNumber)
        } else {
          setPhoneCountryCode(defaultPhoneCode)
          setNeedsMobileNumber(true)
          // console.log('Mobile number missing in profile')
        }

        // Check if all required address fields are present
        const profileNeedsAddress = !isAddressComplete(profile)
        if (profileNeedsAddress) {
          setNeedsAddress(true)
        } else {
          // console.log('Address complete:', {
          //   street: profile.street,
          //   city: profile.city,
          //   state: profile.state,
          //   postalCode: profile.postalCode,
          //   country: profile.country,
          // })
          setNeedsAddress(false)
        }

        await tryIpBasedPrefill({
          profile,
          profileNeedsAddress,
          profileNeedsMobile: !hasProfileMobile,
          resolvedCountryCode,
          defaultPhoneCode,
        })
      } catch (error) {
        console.error('Failed to load profile:', error)
        setError('Failed to load profile. Please refresh the page.')
      }
    }
    if (isAuthenticated && user) {
      checkUserAddress()
    }
  }, [isAuthenticated, user, fetchFreshProfile, tryIpBasedPrefill])

  const loadActiveSubscriptions = useCallback(async () => {
    if (itemType !== 'plan') {
      return []
    }

    try {
      const subscriptions = await apiService.get(
        '/api/v1/subscriptions/my-subscriptions/history',
        {},
        { bypassCache: true, skipCache: true },
      )

      return (subscriptions || []).filter((sub) => sub?.isActive && !sub?.isExpired)
    } catch (err) {
      console.warn('Failed to load active subscriptions:', err)
      return []
    }
  }, [itemType])

  useEffect(() => {
    let isMounted = true

    loadActiveSubscriptions().then((active) => {
      if (isMounted) {
        setActiveSubscriptions(active)
      }
    })

    return () => {
      isMounted = false
    }
  }, [loadActiveSubscriptions, location.key, item?.id, item?.planCode])

  useEffect(() => {
    if (itemType !== 'plan') {
      return undefined
    }

    const refreshOnFocus = async () => {
      const active = await loadActiveSubscriptions()
      setActiveSubscriptions(active)
    }

    window.addEventListener('focus', refreshOnFocus)
    return () => window.removeEventListener('focus', refreshOnFocus)
  }, [itemType, loadActiveSubscriptions])

  const normalizeCountries = useCallback((countries) => {
    return [...new Set((countries || []).map((country) => String(country || '').toUpperCase().trim()))]
      .filter(Boolean)
      .sort()
  }, [])

  const getCoverageScope = useCallback(
    (plan) => {
      const countries = normalizeCountries(plan?.countries)
      const coversAll =
        plan?.planType === 'ALL_COUNTRIES' || countries.includes('ALL')
      const dtaaOnly = Boolean(plan?.dtaaOnly)
      return {
        coversAll,
        countries,
        includesDtaa: Boolean(plan?.includesDtaa || dtaaOnly),
        dtaaOnly,
        includesLinks: plan?.planType !== 'DTAA_ONLY' && dtaaOnly !== true,
      }
    },
    [normalizeCountries],
  )

  const hasSameCoverageScope = useCallback((a, b) => {
    if (a.coversAll !== b.coversAll) {
      return false
    }

    if (!a.coversAll) {
      if (a.countries.length !== b.countries.length) {
        return false
      }
      for (let i = 0; i < a.countries.length; i += 1) {
        if (a.countries[i] !== b.countries[i]) {
          return false
        }
      }
    }

    return a.includesDtaa === b.includesDtaa && a.dtaaOnly === b.dtaaOnly
  }, [])

  const hasCoverageSuperset = useCallback((superset, subset) => {
    const countrySuperset =
      superset.coversAll ||
      (!subset.coversAll &&
        subset.countries.every((country) => superset.countries.includes(country)))
    if (!countrySuperset) {
      return false
    }

    if (subset.includesLinks && !superset.includesLinks) {
      return false
    }

    if (subset.includesDtaa && !superset.includesDtaa) {
      return false
    }

    return true
  }, [])

  const targetScope = useMemo(() => {
    if (itemType !== 'plan' || !item) {
      return null
    }
    return getCoverageScope(item)
  }, [getCoverageScope, item, itemType])

  const overlappingSubscriptions = useMemo(() => {
    if (!targetScope) return []

    const hasCountryOverlap = (a, b) => {
      if (a.coversAll || b.coversAll) return true
      return a.countries.some((country) => b.countries.includes(country))
    }

    const hasFeatureOverlap = (a, b) => {
      const linksOverlap = a.includesLinks && b.includesLinks
      const dtaaOverlap = a.includesDtaa && b.includesDtaa
      return linksOverlap || dtaaOverlap
    }

    return activeSubscriptions.filter((sub) => {
      const subScope = getCoverageScope(sub)
      return hasCountryOverlap(targetScope, subScope) &&
        hasFeatureOverlap(targetScope, subScope)
    })
  }, [activeSubscriptions, getCoverageScope, targetScope])

  const similarScopeOverlaps = useMemo(() => {
    if (!targetScope) {
      return []
    }

    return activeSubscriptions.filter((sub) =>
      hasSameCoverageScope(targetScope, getCoverageScope(sub)),
    )
  }, [activeSubscriptions, getCoverageScope, hasSameCoverageScope, targetScope])

  const supersetCoverageOverlaps = useMemo(() => {
    if (!targetScope) {
      return []
    }

    return activeSubscriptions.filter((sub) =>
      hasCoverageSuperset(getCoverageScope(sub), targetScope),
    )
  }, [activeSubscriptions, getCoverageScope, hasCoverageSuperset, targetScope])

  const queueableDeferredStartDate = useMemo(() => {
    if (supersetCoverageOverlaps.length === 0) {
      return null
    }

    return supersetCoverageOverlaps
      .map((sub) => convertArrayToDate(sub?.endDate))
      .filter(Boolean)
      .sort((a, b) => b.getTime() - a.getTime())[0]
  }, [supersetCoverageOverlaps])

  const hasQueuedSimilarScopeOverlap = useMemo(() => {
    const now = new Date()
    return similarScopeOverlaps.some((sub) => {
      const startDate = convertArrayToDate(sub?.startDate)
      return startDate && startDate.getTime() > now.getTime()
    })
  }, [similarScopeOverlaps])

  const autoRenewSupersetBlocker = useMemo(() => {
    return supersetCoverageOverlaps.find((sub) => {
      return (
        sub?.status === 'Active' &&
        sub?.isRecurring === true &&
        sub?.autoRenew === true
      )
    }) || null
  }, [supersetCoverageOverlaps])

  const canPrepayAgainstAutoRenewBlocker = useMemo(() => {
    if (!autoRenewSupersetBlocker || !targetScope || hasQueuedSimilarScopeOverlap) {
      return false
    }

    const blockerScope = getCoverageScope(autoRenewSupersetBlocker)
    return hasSameCoverageScope(targetScope, blockerScope)
  }, [
    autoRenewSupersetBlocker,
    getCoverageScope,
    hasQueuedSimilarScopeOverlap,
    hasSameCoverageScope,
    targetScope,
  ])

  const formatHumanDate = useCallback((dateInput) => {
    const date = convertArrayToDate(dateInput)
    if (!date) {
      return 'N/A'
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }, [])

  const purchaseBlockMessage = useMemo(() => {
    if (autoRenewSupersetBlocker && !canPrepayAgainstAutoRenewBlocker) {
      const blockerName =
        autoRenewSupersetBlocker?.planName ||
        autoRenewSupersetBlocker?.planCode ||
        'your current plan'
      return `You already have an active auto-renew plan (${blockerName}) that covers this purchase. Disable auto-renew first or wait for the current plan period to end.`
    }

    if (hasQueuedSimilarScopeOverlap) {
      return queueableDeferredStartDate
        ? `You already have a queued plan with similar scope. Please come back after ${formatHumanDate(queueableDeferredStartDate)} when the queued plan expires.`
        : 'You already have a queued plan with similar scope. Please come back later after the queued plan expires.'
    }

    return null
  }, [
    autoRenewSupersetBlocker,
    canPrepayAgainstAutoRenewBlocker,
    formatHumanDate,
    hasQueuedSimilarScopeOverlap,
    queueableDeferredStartDate,
  ])

  const isPurchaseBlocked = Boolean(purchaseBlockMessage)
  const queuePreviewStartDate = isPurchaseBlocked ? null : queueableDeferredStartDate

  const subscriptionAccessWindow = useMemo(() => {
    if (itemType !== 'plan' || !item || isPurchaseBlocked) {
      return null
    }

    const durationDays = Number(item?.durationDays || 0)
    if (!Number.isFinite(durationDays) || durationDays <= 0) {
      return null
    }

    const startDate = queuePreviewStartDate
      ? new Date(queuePreviewStartDate.getTime())
      : new Date()
    const endDate = new Date(startDate.getTime())
    endDate.setDate(endDate.getDate() + durationDays)

    return {
      startDate,
      endDate,
      isQueuedStart: Boolean(queuePreviewStartDate),
    }
  }, [itemType, item, isPurchaseBlocked, queuePreviewStartDate])

  const handleAddressChange = (e) => {
    const { name, value } = e.target
    if (name === 'country' || name === 'state' || name === 'city') {
      geoEditableTouchedRef.current[name] = true
    }

    if (name === 'country') {
      const resolvedCountryCode = resolveCountryCode(value)
      const nextStateOptions = getStateOptions(resolvedCountryCode)
      setAddressData((prev) => ({
        ...prev,
        country: resolvedCountryCode,
        state:
          nextStateOptions.some((state) => state.value === prev.state) &&
          prev.state
            ? prev.state
            : nextStateOptions[0]?.value || '',
      }))
      if (needsMobileNumber) {
        setPhoneCountryCode(getCountryPhoneCode(resolvedCountryCode))
      }
    } else {
      setAddressData((prev) => ({ ...prev, [name]: value }))
    }

    // Clear field error when user starts typing
    if (addressErrors[name] || (name === 'country' && addressErrors.mobileNumber)) {
      setAddressErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        if (name === 'country') {
          delete newErrors.mobileNumber
        }
        return newErrors
      })
    }
  }

  const handleSaveAddress = async () => {
    // Clear previous errors
    setAddressErrors({})
    setError(null)

    // Client-side validation
    const errors = {}

    const normalizedLocalMobile = normalizeLocalMobileNumber(mobileNumber)
    const mobileValidation = validateAndFormatMobileNumber({
      phoneCode: phoneCountryCode,
      localNumber: normalizedLocalMobile,
      fallbackCountryCode: addressData.country,
    })

    // Validate mobile number if needed
    if (needsMobileNumber) {
      if (!normalizedLocalMobile) {
        errors.mobileNumber = 'Mobile number is required'
      } else if (!mobileValidation.isValid) {
        errors.mobileNumber = mobileValidation.error
      }
    }

    // Validate address fields if needed
    if (needsAddress) {
      if (!addressData.street || addressData.street.trim() === '') {
        errors.street = 'Street is required'
      }
      if (!addressData.city || addressData.city.trim() === '') {
        errors.city = 'City is required'
      }
      if (!addressData.state || addressData.state.trim() === '') {
        errors.state = 'State is required'
      }
      if (!addressData.postalCode || addressData.postalCode.trim() === '') {
        errors.postalCode = 'Postal code is required'
      }
      if (!addressData.country || addressData.country.trim() === '') {
        errors.country = 'Country is required'
      }
    }

    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors)
      setError(
        needsAddress && needsMobileNumber
          ? 'Please fill in all required fields'
          : needsAddress
          ? 'Please fill in all required address fields'
          : 'Please fill in mobile number',
      )
      return
    }

    setSavingAddress(true)
    try {
      const currentProfile = await fetchFreshProfile()

      // Determine mobile number to send:
      // 1. If user entered one in the form, use it
      // 2. Otherwise, use existing from profile
      // 3. If needsMobileNumber is true, we must send the form value (even if empty, to clear it)
      const mobileNumberToSave = needsMobileNumber
        ? mobileValidation.storedNumber
        : currentProfile.mobileNumber || ''

      const updateData = {
        name: currentProfile.name, // Required
        mobileNumber: mobileNumberToSave,
        company: currentProfile.company, // Optional
        location: currentProfile.location, // Optional
        bio: currentProfile.bio, // Optional

        // Only include address fields if address is being updated
        ...(needsAddress && {
          street: addressData.street.trim(),
          city: addressData.city.trim(),
          state: addressData.state.trim(),
          postalCode: addressData.postalCode.trim(),
          country: resolveCountryCode(addressData.country),
        }),
      }

      // console.log('Saving profile with updateData:', updateData)
      // Save address and/or mobile number to user profile
      const updatedProfile = await apiService.put('/api/v1/profile', updateData)
      apiService.clearEndpointCache('/api/v1/profile')

      // console.log('Profile updated successfully:', updatedProfile)
      // Re-check what's needed based on the updated profile response
      // This ensures we reflect the actual saved state
      let profileToCheck = updatedProfile

      if (!profileToCheck || typeof profileToCheck !== 'object') {
        // Fallback: re-fetch profile to verify what was saved
        // console.log('Response is not an object, re-fetching profile...')
        try {
          profileToCheck = await fetchFreshProfile()
          // console.log('Refreshed profile:', profileToCheck)
        } catch (refreshError) {
          console.error('Failed to refresh profile after save:', refreshError)
          // Still clear flags optimistically if save succeeded
          setNeedsAddress(false)
          setNeedsMobileNumber(false)
          setError(null)
          setAddressErrors({})
          return
        }
      }

      // Check mobile number
      const savedMobileNumber = profileToCheck.mobileNumber
      if (
        savedMobileNumber &&
        typeof savedMobileNumber === 'string' &&
        savedMobileNumber.trim() !== ''
      ) {
        // console.log('Mobile number found in response:', savedMobileNumber)
        const resolvedCountryCode = resolveCountryCode(
          profileToCheck.country || addressData.country,
        )
        const fallbackPhoneCode = getCountryPhoneCode(resolvedCountryCode)
        const normalizedStored = splitStoredMobileNumber(
          savedMobileNumber,
          fallbackPhoneCode,
        )
        setPhoneCountryCode(normalizedStored.phoneCode || fallbackPhoneCode)
        setNeedsMobileNumber(false)
        setMobileNumber(normalizedStored.localNumber)
      } else {
        // console.log(
        //   'Mobile number still missing or invalid in response:',
        //   savedMobileNumber,
        // )
        // Only set needsMobileNumber to true if we actually needed it
        // Don't change it if it was already false
      }

      // Check address
      if (isAddressComplete(profileToCheck)) {
        // console.log('Address is complete in response')
        setNeedsAddress(false)
      } else {
        // console.log('Address still incomplete in response')
        // Only set needsAddress to true if we actually needed it
      }

      setError(null)
      setAddressErrors({})
    } catch (err) {
      console.error('Failed to save address:', err)
      console.error('Error response:', err.response?.data)
      console.error('Error status:', err.response?.status)
      console.error('Full error object:', JSON.stringify(err, null, 2))
      console.error('Error keys:', Object.keys(err))
      const errorData = err.response?.data
      const statusCode = err.response?.status

      const fieldErrors = extractCheckoutFieldErrors(errorData)
      if (statusCode === 400 && applyCheckoutFieldErrors(fieldErrors)) {
        return
      }

      // Handle generic backend error formats
      if (errorData?.message) {
        const backendMessage = errorData.message
        const normalizedMessage = backendMessage.toLowerCase()
        setError(backendMessage)

        if (errorData.requiresAddress || normalizedMessage.includes('address')) {
          setNeedsAddress(true)
        }
        if (normalizedMessage.includes('mobile')) {
          setNeedsMobileNumber(true)
        }

        setTimeout(() => {
          if (normalizedMessage.includes('mobile')) {
            focusCheckoutField('mobileNumber')
            return
          }

          document.getElementById('address-form')?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }, 100)
      } else if (errorData?.error) {
        setError(errorData.error)
      } else if (errorData && typeof errorData === 'object') {
        const fallbackFieldErrors = extractCheckoutFieldErrors(errorData)
        if (!applyCheckoutFieldErrors(fallbackFieldErrors)) {
          const errorMsg = JSON.stringify(errorData)
          console.warn('Unhandled error format:', errorData)
          setError(`Validation failed: ${errorMsg}`)
          setNeedsAddress(true)
        }
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.userMessage ||
          err.message ||
          'Failed to save details. Please try again.'
        setError(errorMsg)
        if (!needsAddress && !needsMobileNumber) {
          setNeedsAddress(true)
        }
      }

      if (!needsAddress && !needsMobileNumber) {
        setNeedsAddress(true)
      }
    } finally {
      setSavingAddress(false)
    }
  }
  // Get price with field name fallback
  const getPrice = () => item.priceAed || item.price || 0

  // Get item name with field name fallback
  const getItemName = () => {
    if (itemType === 'plan') return item.planName || 'Subscription Plan'
    return item.packageName || 'Credit Package'
  }

  const resolveCouponErrorMessage = useCallback((err) => {
    const errorData = err?.response?.data || {}
    return (
      (typeof errorData?.reason === 'string' && errorData.reason) ||
      (typeof errorData?.message === 'string' && errorData.message) ||
      (typeof err?.userMessage === 'string' && err.userMessage) ||
      'Invalid coupon code'
    )
  }, [])

  const handleClearCoupon = useCallback(() => {
    setAppliedCoupon(null)
    setCouponError(null)
  }, [])

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return
    setCouponError(null)

    try {
      const response = await apiService.post('/api/v1/coupons/validate', {
        couponCode: couponCode.trim(),
        purchaseType: itemType === 'plan' ? 'SUBSCRIPTION' : 'CREDITS',
        amount: getPrice(),
        ...(itemType === 'plan' ? { planId: item.id } : {}),
      })

      if (response?.valid === false) {
        throw new Error(response?.message || 'Invalid coupon code')
      }

      setAppliedCoupon({
        ...response,
        couponCode: response?.couponCode || couponCode.trim().toUpperCase(),
      })
      setCouponError(null)
      setError(null)
    } catch (err) {
      setCouponError(resolveCouponErrorMessage(err))
      setError(null)
      setAppliedCoupon(null)
    }
  }
  const handleSubmit = async (event) => {
    event.preventDefault()

    if (submitInFlightRef.current || processing || success) {
      return
    }

    // ============================================================================
    // VALIDATION
    // ============================================================================
    if (needsAddress || needsMobileNumber) {
      const blockerMessage =
        needsAddress && needsMobileNumber
          ? 'Please complete your address and mobile number before proceeding.'
          : needsAddress
          ? 'Please complete your address information below before proceeding.'
          : 'Please add your mobile number before proceeding.'

      setError(blockerMessage)

      setTimeout(() => {
        if (needsMobileNumber && !needsAddress) {
          focusCheckoutField('mobileNumber')
          return
        }

        document.getElementById('address-form')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 100)
      return
    }
    if (purchaseBlockMessage) {
      setError(purchaseBlockMessage)
      return
    }
    if (!stripe || !elements) {
      setError('Payment system not loaded. Please refresh the page.')
      return
    }

    if (!agreeTerms) {
      setError('Please agree to the terms and conditions')
      return
    }

    if (!item.id) {
      setError('Item ID is missing. Please go back and select a plan/package.')
      return
    }
    if (itemType === 'plan' && !item?.planCode) {
      setError('Plan code is missing. Please go back and select a plan again.')
      return
    }

    submitInFlightRef.current = true
    setProcessing(true)
    setError(null)
    setCouponError(null)
    setSuccess(false)

    try {
      // ============================================================================
      // STEP 1: GET CARD ELEMENT
      // ============================================================================
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card details not found')
      }

      // console.log('=== Payment Flow Started ===')
      // console.log('Item Type:', itemType)
      // console.log('Item ID:', item.id)

      // ============================================================================
      // STEP 2: CREATE PAYMENT METHOD
      // ============================================================================
      // console.log('Creating payment method...')
      const { error: stripeError, paymentMethod: stripePaymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            email: user?.email || '',
            name: user?.name || '',
          },
        })

      if (stripeError) {
        console.error('Payment method creation failed:', stripeError)
        const paymentMethodError = new Error(stripeError.message)
        paymentMethodError.code = stripeError.code
        paymentMethodError.decline_code = stripeError.decline_code
        paymentMethodError.raw = stripeError.raw
        throw paymentMethodError
      }

      // console.log('Payment method created:', stripePaymentMethod.id)

      // ============================================================================
      // STEP 3: CREATE PAYMENT ON BACKEND
      // ============================================================================
      const paymentData = {
        itemType: itemType === 'plan' ? 'SUBSCRIPTION' : 'CREDIT_PACKAGE',
        ...(itemType === 'plan'
          ? { planCode: item.planCode }
          : { packageId: item.id }),
        paymentGateway: 'STRIPE',
        paymentMethodId: stripePaymentMethod.id,
        ...(itemType === 'plan' && {
          savePaymentMethod: Boolean(item?.isRecurring) && autoPayOptIn,
        }),
        couponCode: appliedCoupon?.couponCode || appliedCoupon?.code || null,
        successUrl: buildCheckoutContextUrl('/checkout/success'),
        cancelUrl: buildCheckoutContextUrl('/checkout', true),
      }

      // console.log('Creating payment with data:', paymentData)

      const response = await apiService.post(
        '/api/v1/payments/create',
        paymentData,
      )

      // console.log('Payment created on backend:', {
      //   paymentId: response.paymentId,
      //   clientSecret: response.clientSecret ? 'Present' : 'Missing',
      // })

      if (!response.clientSecret) {
        throw new Error('Client secret not received from backend')
      }

      // ============================================================================
      // STEP 4: CONFIRM PAYMENT WITH STRIPE (Handles both 3DS and non-3DS)
      // ============================================================================
      // console.log('Confirming payment with Stripe...')

      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(response.clientSecret, {
          payment_method: stripePaymentMethod.id,
        })

      if (confirmError) {
        console.error('Payment confirmation failed:', confirmError)
        const confirmationError = new Error(confirmError.message)
        confirmationError.code = confirmError.code
        confirmationError.decline_code = confirmError.decline_code
        confirmationError.raw = confirmError.raw
        throw confirmationError
      }

      // console.log('Payment confirmed! Status:', paymentIntent.status)
      // console.log('PaymentIntent ID:', paymentIntent.id)

      // ============================================================================
      // STEP 5: VERIFY PAYMENT STATUS
      // ============================================================================
      if (paymentIntent.status === 'succeeded') {
        // console.log('Payment succeeded! Verifying with backend...')

        try {
          const verifyResponse = await apiService.post(
            '/api/v1/payments/verify',
            {
              paymentIntentId: paymentIntent.id,
            },
          )

          // console.log('Payment verified:', verifyResponse)

          // ============================================================================
          // STEP 6: SUCCESS - REFRESH QUOTA & REDIRECT
          // ============================================================================
          setSuccess(true)

          // Refresh user quota
          if (refreshQuota) {
            // console.log('Refreshing quota...')
            await refreshQuota()
          }

          // console.log('=== Payment Flow Completed Successfully ===')
          // Redirect to success page
          const subscriptionId = verifyResponse.subscriptionId
          const creditsAdded = verifyResponse.creditsAdded

          // Use navigate with state for better UX
          setTimeout(() => {
            navigate('/checkout/success', {
              state: {
                itemType,
                itemName: getItemName(),
                amount:
                  typeof verifyResponse?.amountPaid === 'number'
                    ? verifyResponse.amountPaid
                    : calculateFinalAmount(),
                subscriptionId: subscriptionId,
                creditsAdded: creditsAdded,
                paymentId: response.paymentId,
                planCode: itemType === 'plan' ? item.planCode : null,
                packageId: itemType === 'package' ? item.id : null,
                userSubscription: verifyResponse?.userSubscription || null,
                completedAt: verifyResponse?.completedAt || null,
              },
            })
          }, 1000)
        } catch (verifyError) {
          console.error('Payment verification failed:', verifyError)

          // Payment succeeded on Stripe but backend verification failed
          // Show user-friendly message with instructions
          setError(
            'Payment succeeded but verification failed. ' +
              'Your subscription will be activated shortly. ' +
              'Please check your profile in a few minutes or contact support@gcctaxlaws.com if the issue persists.',
          )

          // Still redirect to success after delay (payment did succeed)
          setTimeout(() => {
            navigate('/profile?tab=subscriptions')
          }, 5000)
        }
      } else if (paymentIntent.status === 'requires_payment_method') {
        // Payment failed - requires new payment method
        throw new Error(
          'Payment failed. Please try again with a different card.',
        )
      } else if (paymentIntent.status === 'processing') {
        // Payment is processing (rare for cards, common for bank transfers)
        setError(
          'Payment is being processed. You will receive an email confirmation once completed. ' +
            'This may take a few minutes.',
        )

        setTimeout(() => {
          navigate('/profile?tab=subscriptions')
        }, 3000)
      } else {
        // Unknown status
        throw new Error(
          `Payment status: ${paymentIntent.status}. Please contact support@gcctaxlaws.com.`,
        )
      }
    } catch (err) {
      console.error('=== Payment Flow Failed ===')
      console.error('Payment Error:', err)
      submitInFlightRef.current = false

      // User-friendly error messages
      let errorMessage = 'Payment failed. Please try again.'

      const errorData = err.response?.data
      const fieldErrors = extractCheckoutFieldErrors(errorData)
      if (applyCheckoutFieldErrors(fieldErrors)) {
        setProcessing(false)
        setSuccess(false)
        return
      }

      const backendMessage =
        (typeof errorData?.message === 'string' && errorData.message) ||
        (typeof errorData?.error === 'string' && errorData.error) ||
        (typeof err.message === 'string' ? err.message : '') ||
        ''
      const stripeFailureMessage = mapStripeFailureMessage(err)
      const normalizedBackendMessage = backendMessage.toLowerCase()
      const isCouponError =
        errorData?.error === 'Invalid Coupon' ||
        Boolean(errorData?.couponCode) ||
        normalizedBackendMessage.includes('coupon')
      const isAddressError =
        normalizedBackendMessage.includes('address') ||
        errorData?.requiresAddress === true
      const isMobileError =
        normalizedBackendMessage.includes('mobile') ||
        normalizedBackendMessage.includes('phone')

      if (isCouponError) {
        const couponFailureMessage = resolveCouponErrorMessage(err)
        setAppliedCoupon(null)
        setCouponError(couponFailureMessage)
        errorMessage = couponFailureMessage
      } else if (isAddressError || isMobileError) {
        if (isAddressError) {
          setNeedsAddress(true)
        }
        if (isMobileError) {
          setNeedsMobileNumber(true)
        }

        errorMessage =
          backendMessage ||
          (isAddressError && isMobileError
            ? 'Address and mobile number are required before payment.'
            : isAddressError
            ? 'Complete address information is required before payment.'
            : 'A valid mobile number is required before payment.')

        setTimeout(() => {
          if (isMobileError && !isAddressError) {
            focusCheckoutField('mobileNumber')
            return
          }

          document.getElementById('address-form')?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }, 100)
      } else if (err.message?.toLowerCase().includes('card')) {
        errorMessage = stripeFailureMessage || err.message
      } else if (stripeFailureMessage) {
        errorMessage = stripeFailureMessage
      } else if (backendMessage) {
        errorMessage = backendMessage
      }

      setError(errorMessage)
      setProcessing(false)
      setSuccess(false)
    }
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: themeColors.primary,
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#f44336',
      },
    },
    hidePostalCode: true,
    // disableLink: true, // auto save card details feature disabled to avoid confusion with address form
    ...(process.env.REACT_APP_ENVIRONMENT === 'dev' && {
      hideIcon: false,
    }),
  }

  const parseAmount = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  const formatAed = (amount) => {
    const safeAmount = parseAmount(amount) ?? 0
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeAmount)
  }

  const couponPricingPreview = (() => {
    const basePrice = parseAmount(getPrice()) ?? 0
    const vatMultiplier = process.env.REACT_APP_CHARGE_VAT === 'true' ? 1.05 : 1
    const fallbackWithoutCoupon = basePrice * vatMultiplier

    if (!appliedCoupon) {
      return {
        hasCoupon: false,
        originalAmount: basePrice,
        discountAmount: 0,
        finalAmount: fallbackWithoutCoupon,
        discountDescription: null,
      }
    }

    const originalAmount =
      parseAmount(appliedCoupon?.originalAmount) ?? basePrice
    const discountAmount = Math.max(
      0,
      parseAmount(appliedCoupon?.discountAmount) ??
        parseAmount(appliedCoupon?.discountValue) ??
        0,
    )
    const finalAmountFromApi = parseAmount(appliedCoupon?.finalAmount)
    const fallbackFromDiscount = Math.max(0, originalAmount - discountAmount)

    return {
      hasCoupon: true,
      originalAmount,
      discountAmount,
      finalAmount:
        (finalAmountFromApi ?? fallbackFromDiscount) * vatMultiplier,
      discountDescription: appliedCoupon?.discountDescription || null,
    }
  })()

  const calculateFinalAmount = () => {
    return parseAmount(couponPricingPreview.finalAmount) ?? 0
  }

  return (
    <Paper
      sx={{
        p: { xs: 2.25, sm: 3, md: 4 },
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        ...formFocusStyles,
      }}
    >
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 3 }}>
        Payment Details
      </Typography>

      {/* {error && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )} */}

      <Snackbar
        open={Boolean(error)}
        onClose={handlePaymentErrorSnackbarClose}
        autoHideDuration={7000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity='error'
          onClose={handlePaymentErrorSnackbarClose}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* {processing && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          {navigationAttemptBlocked
            ? PAYMENT_NAVIGATION_BLOCKED_MESSAGE
            : PAYMENT_IN_PROGRESS_WARNING_MESSAGE}
        </Alert>
      )} */}

      {/* {success && (
        <Alert severity='success' sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Payment successful! Redirecting...</Typography>
          </Box>
        </Alert>
      )} */}

      <Snackbar
        open={Boolean(paymentStatusSnackbar)}
        onClose={handlePaymentStatusSnackbarClose}
        autoHideDuration={paymentStatusSnackbar?.autoHideDuration ?? null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={paymentStatusSnackbar?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {paymentStatusSnackbar?.showSpinner ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color='inherit' />
              <Typography variant='body2'>
                {paymentStatusSnackbar.message}
              </Typography>
            </Box>
          ) : (
            paymentStatusSnackbar?.message
          )}
        </Alert>
      </Snackbar>
      {overlappingSubscriptions.length > 0 && (
        <Alert
          severity='warning'
          sx={{
            mb: 3,
            bgcolor: 'rgba(255, 207, 81, 0.2)',
            border: `1px solid ${themeColors.secondary}`,
            color: themeColors.primary,
            '& .MuiAlert-icon': {
              color: themeColors.primary,
            },
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
            You already have an active plan that overlaps with this purchase.
          </Typography>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {isPurchaseBlocked
              ? 'This purchase is currently blocked since you already have similar scope plan in `active` and `scheduled` state'
              : 'You can still continue...'}
          </Typography>
          {autoRenewSupersetBlocker ? (
            canPrepayAgainstAutoRenewBlocker ? (
              <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 600 }}>
                You can prepay this renewal now. The new plan will be queued to
                start after your current plan ends, and your current active plan
                will be marked as cancelled while remaining active until it
                ends.
              </Typography>
            ) : (
              <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 600 }}>
                Purchase is blocked because{' '}
                {autoRenewSupersetBlocker.planName ||
                  autoRenewSupersetBlocker.planCode ||
                  'your active auto-renew plan'}{' '}
                already covers this scope.
              </Typography>
            )
          ) : hasQueuedSimilarScopeOverlap ? (
            <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 600 }}>
              To avoid duplicate coverage, please come back after{' '}
              {queueableDeferredStartDate
                ? formatHumanDate(queueableDeferredStartDate)
                : 'the queued coverage ends'}{' '}
              to make a new purchase.
            </Typography>
          ) : (
            queuePreviewStartDate && (
              <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 600 }}>
                If you continue, this purchase will be added to your account and
                will start on {formatHumanDate(queuePreviewStartDate)},
                immediately after your currently active similar plan ends.
              </Typography>
            )
          )}
          <List dense>
            {overlappingSubscriptions.map((sub) => (
              <ListItem key={sub.id} sx={{ px: 0, py: 0.25 }}>
                <ListItemText
                  primary={sub.planName || sub.planCode || 'Active plan'}
                  secondary={`${
                    sub.expiryMessage || sub.status
                  }${sub.endDate ? ` - Ends ${formatHumanDate(sub.endDate)}` : ''}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
      {itemType === 'plan' && subscriptionAccessWindow && (
        <Alert
          severity='info'
          icon={false}
          sx={{
            mb: 3,
            bgcolor: 'rgba(35, 37, 54, 0.06)',
            border: '1px solid rgba(35, 37, 54, 0.2)',
            color: themeColors.primary,
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.75 }}>
            Subscription access period for this purchase
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.25 }}>
            Starts: {formatHumanDate(subscriptionAccessWindow.startDate)}
          </Typography>
          <Typography variant='body2'>
            Ends: {formatHumanDate(subscriptionAccessWindow.endDate)}
          </Typography>
          {subscriptionAccessWindow.isQueuedStart && !isPurchaseBlocked && (
            <Typography variant='caption' sx={{ mt: 0.75, display: 'block' }}>
              Start date is queued after your currently active similar plan gets
              over.
            </Typography>
          )}
          {/* {isPurchaseBlocked && (
            <Typography variant='caption' sx={{ mt: 0.75, display: 'block' }}>
              Purchase is currently blocked by your current subscription
              configuration.
            </Typography>
          )} */}
        </Alert>
      )}
      {(needsAddress || needsMobileNumber) && (
        <Paper
          id='address-form'
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            bgcolor: 'rgba(255, 207, 81, 0.2)',
            border: `1px solid ${themeColors.secondary}`,
          }}
        >
          <Typography
            variant='h6'
            sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <LocationOn sx={{ color: themeColors.primary }} />
            {needsAddress && needsMobileNumber
              ? 'Address & Mobile Number Required'
              : needsAddress
                ? 'Address Required'
                : 'Mobile Number Required'}
          </Typography>
          <Typography variant='body2' sx={{ mb: 2, color: '#666' }}>
            {needsAddress && needsMobileNumber
              ? 'Please complete these details so payment can continue.'
              : needsAddress
                ? 'Please complete your address so payment can continue.'
                : 'Please add a valid mobile number so payment can continue.'}
          </Typography>
          <Box sx={{ mb: 2, color: '#4b4b4b' }}>
            {/* {needsMobileNumber && (
              <Typography variant='caption' sx={{ display: 'block' }}>
                Mobile: choose country code first, then enter the remaining number
                only (up to {MOBILE_NUMBER_LOCAL_MAX_DIGITS} digits).
              </Typography>
            )}
            {needsAddress && (
              <Typography variant='caption' sx={{ display: 'block' }}>
                Address: street should be 5-150 characters, city/state 2-100
                characters, and postal code 3-20 letters or numbers.
              </Typography>
            )} */}
          </Box>

          <Grid container spacing={2}>
            {needsMobileNumber && (
              <>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Code</InputLabel>
                    <Select
                      label='Code'
                      value={phoneCountryCode}
                      onChange={(e) => {
                        geoEditableTouchedRef.current.phoneCountryCode = true
                        setPhoneCountryCode(
                          normalizeLocalMobileNumber(e.target.value),
                        )
                        if (addressErrors.mobileNumber) {
                          setAddressErrors((prev) => {
                            const nextErrors = { ...prev }
                            delete nextErrors.mobileNumber
                            return nextErrors
                          })
                        }
                      }}
                    >
                      {PHONE_CODE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label='Mobile Number'
                    name='mobileNumber'
                    value={mobileNumber}
                    onChange={(e) => {
                      setMobileNumber(
                        normalizeLocalMobileNumber(e.target.value),
                      )
                      if (addressErrors.mobileNumber) {
                        setAddressErrors((prev) => {
                          const nextErrors = { ...prev }
                          delete nextErrors.mobileNumber
                          return nextErrors
                        })
                      }
                    }}
                    required={needsMobileNumber}
                    placeholder='Enter local number'
                    error={!!addressErrors.mobileNumber}
                    helperText={
                      addressErrors.mobileNumber ||
                      `Enter only the number part (no + or country code), max ${MOBILE_NUMBER_LOCAL_MAX_DIGITS} digits`
                    }
                    inputProps={{ maxLength: MOBILE_NUMBER_LOCAL_MAX_DIGITS }}
                  />
                </Grid>
              </>
            )}
            {needsAddress && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Street Address'
                    name='street'
                    value={addressData.street}
                    onChange={handleAddressChange}
                    required
                    placeholder='123 Main Street, Apartment 4B'
                    error={!!addressErrors.street}
                    helperText={
                      addressErrors.street ||
                      'Use 5-150 characters with house/building and area details'
                    }
                    inputProps={{ minLength: 5, maxLength: 150 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label='City'
                    name='city'
                    value={addressData.city}
                    onChange={handleAddressChange}
                    required
                    error={!!addressErrors.city}
                    helperText={
                      addressErrors.city ||
                      'Use 2-100 characters (for example: Dubai, Mumbai)'
                    }
                    inputProps={{ minLength: 2, maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!addressErrors.state}>
                    <InputLabel>State/Province</InputLabel>
                    <Select
                      label='State/Province'
                      name='state'
                      value={addressData.state}
                      onChange={handleAddressChange}
                      required
                    >
                      {stateOptions.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {addressErrors.state && (
                      <Typography
                        variant='caption'
                        sx={{ color: 'error.main', mt: 0.75, ml: 1.75 }}
                      >
                        {addressErrors.state}
                      </Typography>
                    )}
                    {!addressErrors.state && (
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary', mt: 0.75, ml: 1.75 }}
                      >
                        Select a state or province
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label='Postal Code'
                    name='postalCode'
                    value={addressData.postalCode}
                    onChange={handleAddressChange}
                    required
                    error={!!addressErrors.postalCode}
                    helperText={
                      addressErrors.postalCode ||
                      'Use 3-20 letters or numbers (spaces and hyphen allowed)'
                    }
                    inputProps={{ minLength: 3, maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!addressErrors.country}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      label='Country'
                      name='country'
                      value={addressData.country}
                      onChange={handleAddressChange}
                      required
                    >
                      {COUNTRY_OPTIONS.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {addressErrors.country && (
                      <Typography
                        variant='caption'
                        sx={{ color: 'error.main', mt: 0.75, ml: 1.75 }}
                      >
                        {addressErrors.country}
                      </Typography>
                    )}
                    {!addressErrors.country && (
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary', mt: 0.75, ml: 1.75 }}
                      >
                        Select the country for this address
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Button
                variant='contained'
                fullWidth
                onClick={handleSaveAddress}
                disabled={
                  savingAddress ||
                  (needsAddress &&
                    (!addressData.street?.trim() ||
                      !addressData.city?.trim() ||
                      !addressData.state?.trim() ||
                      !addressData.postalCode?.trim() ||
                      !addressData.country?.trim())) ||
                  !isMobileNumberValidForSubmit
                }
                sx={{
                  bgcolor: themeColors.primary,
                  color: themeColors.secondary,
                  '&:hover': { bgcolor: themeColors.hover },
                  '&:disabled': { bgcolor: '#e0e0e0' },
                }}
              >
                {savingAddress ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 1, color: 'white' }}
                    />
                    Saving...
                  </>
                ) : needsAddress && needsMobileNumber ? (
                  'Save & Continue'
                ) : needsAddress ? (
                  'Save Address & Continue'
                ) : (
                  'Save Mobile Number & Continue'
                )}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <form onSubmit={handleSubmit}>
        {/* Coupon Code */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
            Have a coupon code?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'stretch' },
            }}
          >
            <TextField
              fullWidth
              size='small'
              placeholder='Enter coupon code'
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase())
                if (couponError) {
                  setCouponError(null)
                }
              }}
              disabled={!!appliedCoupon || processing}
              error={Boolean(couponError)}
            />
            <Button
              variant='outlined'
              onClick={handleApplyCoupon}
              disabled={!!appliedCoupon || processing || !couponCode.trim()}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: 100,
                color: themeColors.primary,
                borderColor: 'rgba(35, 37, 54, 0.35)',
                '&:hover': {
                  borderColor: themeColors.primary,
                  bgcolor: 'rgba(35, 37, 54, 0.04)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(35, 37, 54, 0.2)',
                },
              }}
            >
              {appliedCoupon ? 'Applied' : 'Apply'}
            </Button>
            {appliedCoupon && (
              <Button
                variant='text'
                onClick={handleClearCoupon}
                disabled={processing}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: 80,
                  color: themeColors.primary,
                  '&:hover': {
                    backgroundColor: 'rgba(35, 37, 54, 0.08)',
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
          {/* {couponError && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {couponError}
            </Alert>
          )} */}
          {appliedCoupon && (
            <Alert severity='success' sx={{ mt: 1 }}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                Coupon applied!{' '}
                {couponPricingPreview.discountDescription ||
                  `You save ${formatAed(couponPricingPreview.discountAmount)}.`}
              </Typography>
              <Typography variant='caption' sx={{ display: 'block', mt: 0.5 }}>
                Original: {formatAed(couponPricingPreview.originalAmount)} |
                Discount: {formatAed(couponPricingPreview.discountAmount)} | New
                total: {formatAed(couponPricingPreview.finalAmount)}
              </Typography>
            </Alert>
          )}
          <Snackbar
            open={couponErrorSnackbarOpen && Boolean(couponError)}
            onClose={handleCouponErrorSnackbarClose}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              severity='error'
              onClose={handleCouponErrorSnackbarClose}
              sx={{ width: '100%' }}
            >
              {couponError}
            </Alert>
          </Snackbar>
          {couponPricingPreview.hasCoupon && (
            <Box
              sx={{
                mt: 1.25,
                p: 1.5,
                borderRadius: 1,
                border: '1px dashed rgba(35, 37, 54, 0.35)',
                bgcolor: 'rgba(35, 37, 54, 0.03)',
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                Coupon Impact
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Original amount
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ textDecoration: 'line-through' }}
                >
                  {formatAed(couponPricingPreview.originalAmount)}
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 0.25,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Coupon discount
                </Typography>
                <Typography variant='body2' sx={{ color: 'success.dark' }}>
                  -{formatAed(couponPricingPreview.discountAmount)}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.75 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Amount to pay
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {formatAed(couponPricingPreview.finalAmount)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Payment Method Selection */}
        {/* <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
            Payment Method
          </Typography>
          <RadioGroup
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <FormControlLabel
              value='stripe'
              control={
                <Radio icon={<span />} checkedIcon={<span />} sx={{ p: 0 }} />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    border: '2px solid',
                    // Border changes to your dark color when selected
                    borderColor:
                      paymentMethod === 'stripe'
                        ? themeColors.primary
                        : '#e0e0e0',
                    borderRadius: 2,
                    width: '100%',
                    transition: 'all 0.2s ease',
                    backgroundColor:
                      paymentMethod === 'stripe'
                        ? 'rgba(35, 37, 54, 0.04)'
                        : 'transparent',
                    '&:hover': {
                      borderColor: themeColors.primary,
                    },
                  }}
                >
                  <CreditCard sx={{ color: themeColors.primary }} />
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500, color: themeColors.primary }}
                  >
                    Credit/Debit Card
                  </Typography>
                </Box>
              }
              sx={{
                width: '100%',
                m: 0, // Removes default label margins
                '& .MuiFormControlLabel-label': { width: '100%' },
              }}
            />
          </RadioGroup>
        </Box> */}

        {/* Card Details */}
        {paymentMethod === 'stripe' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
              Card Details
            </Typography>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: 'white',
              }}
            >
              <CardElement
                options={{
                  ...cardElementOptions,
                  disabled: processing || success,
                }}
              />
            </Box>
            {process.env.REACT_APP_ENVIRONMENT === 'dev' && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 1, display: 'block' }}
              >
                Test card details:
                <br />- 4242 4242 4242 4242
                <br />- Any future date
                <br />- Any 3-digit CVC
              </Typography>
            )}
          </Box>
        )}
        {itemType === 'plan' && Boolean(item?.isRecurring) && (
          <Paper
            sx={{
              p: { xs: 1.75, sm: 2 },
              mb: 3,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              bgcolor: '#fafafa',
            }}
          >
            <FormControlLabel
              sx={{
                alignItems: 'flex-start', // Keeps checkbox at the top
                m: 0, // Removes default margin
              }}
              control={
                <Checkbox
                  checked={autoPayOptIn}
                  onChange={(e) => setAutoPayOptIn(e.target.checked)}
                  disabled={processing}
                  sx={{
                    py: 0, // Removes top padding from checkbox to align with first line
                    color: themeColors.primary,
                    '&.Mui-checked': {
                      color: themeColors.primary,
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(35, 37, 54, 0.04)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(35, 37, 54, 0.28)',
                    },
                    '&.Mui-checked.Mui-disabled': {
                      color: 'rgba(35, 37, 54, 0.28)',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, lineHeight: 1.5 }}
                  >
                    Configure AutoPay for this recurring plan
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}
                  >
                    Enabled by default. If checked, this plan starts with
                    auto-renew ON. If unchecked, auto-renew remains OFF, you can
                    enable auto-renew later from Profile.
                  </Typography>
                </Box>
              }
            />
          </Paper>
        )}

        {/* Terms and Conditions */}
        <FormControlLabel
          control={
            <Checkbox
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              disabled={processing}
              sx={{
                p: 0.75,
                color: themeColors.primary, // Color when unchecked
                '&.Mui-checked': {
                  color: themeColors.primary, // Color when checked
                },
                '&:hover': {
                  backgroundColor: 'rgba(35, 37, 54, 0.04)', // Subtle dark hover ripple
                },
              }}
            />
          }
          label={
            <Typography
              variant='body2'
              sx={{
                lineHeight: 1.5,
                '& a': {
                  color: themeColors.primary,
                  textDecoration: 'underline',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease-in-out',
                },
                '& a:hover': {
                  backgroundColor: themeColors.primary,
                  color: themeColors.secondary,
                  textDecoration: 'none',
                },
              }}
            >
              I agree to the{' '}
              <a
                href='/terms-and-conditions'
                target='_blank'
                rel='noopener noreferrer'
              >
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                href='/privacy-policy'
                target='_blank'
                rel='noopener noreferrer'
              >
                Privacy Policy
              </a>
            </Typography>
          }
          sx={{
            mb: 2,
            mx: 0,
            alignItems: 'center',
            '& .MuiFormControlLabel-label': {
              lineHeight: 1.5,
            },
          }}
        />

        <Alert
          severity='info'
          icon={false}
          sx={{
            mt: 0.5,
            mb: 2,
            bgcolor: 'rgba(35, 37, 54, 0.06)',
            border: '1px solid rgba(35, 37, 54, 0.2)',
            color: themeColors.primary,
          }}
        >
          <Typography
            variant='caption'
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Lock sx={{ fontSize: 14, mr: 0.5 }} />
            Your payment is secure and encrypted, powered by Stripe.
          </Typography>
        </Alert>

        {/* Submit Button */}
        <Button
          type='submit'
          variant='contained'
          fullWidth
          size='large'
          disabled={
            !stripe ||
            processing ||
            success ||
            !agreeTerms ||
            needsAddress ||
            needsMobileNumber ||
            isPurchaseBlocked
          }
          startIcon={
            processing ? <CircularProgress size={20} /> : <ShoppingCart />
          }
          sx={{
            bgcolor: themeColors.primary,
            color: themeColors.secondary,
            mt: 0.25,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            '&:hover': {
              bgcolor: themeColors.hover,
            },
            '&:disabled': {
              bgcolor: '#e0e0e0',
            },
          }}
        >
          {processing
            ? 'Processing...'
            : `Pay AED ${calculateFinalAmount().toFixed(2)}`}
        </Button>
      </form>

      <Dialog
        open={leaveDialogOpen}
        onClose={handleStayOnCheckout}
        aria-labelledby='checkout-leave-dialog-title'
        aria-describedby='checkout-leave-dialog-description'
      >
        <DialogTitle id='checkout-leave-dialog-title'>
          Leave Payment Flow?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='checkout-leave-dialog-description'>
            Your payment is still in progress. Leaving now can interrupt payment
            confirmation. Stay on this page until processing completes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleStayOnCheckout}
            variant='outlined'
            sx={{
              borderColor: themeColors.primary,
              color: themeColors.primary,
              '&:hover': { borderColor: themeColors.hover, color: themeColors.hover },
            }}
          >
            Keep Paying
          </Button>
          <Button
            onClick={handleLeaveCheckout}
            variant='contained'
            sx={{
              bgcolor: themeColors.primary,
              color: '#ffffff',
              '&:hover': { bgcolor: themeColors.hover },
            }}
          >
            Leave Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default Checkout
